/**
 * Incident clock — supports real-time operation and a scrub/step mode for
 * tabletop facilitation. Duration-based insights (e.g. "life risk unresolved
 * for N minutes") read elapsed time from here.
 *
 * Pure: `now()` takes the real wall-clock millis as an argument so every
 * derivation is deterministic and testable.
 */

export type ClockMode = 'real' | 'scrub';

export interface IncidentClock {
  readonly mode: ClockMode;
  /** ISO timestamp of incident T0 (e.g. time of dispatch). */
  readonly startedAt: string;
  /**
   * In `scrub` mode, the simulated elapsed seconds since `startedAt`.
   * Ignored in `real` mode.
   */
  readonly scrubElapsedSec: number;
}

export function createClock(startedAt: string = new Date().toISOString()): IncidentClock {
  return { mode: 'real', startedAt, scrubElapsedSec: 0 };
}

/** Current incident time in epoch millis, given the real wall clock. */
export function now(clock: IncidentClock, realNowMs: number): number {
  if (clock.mode === 'scrub') {
    return Date.parse(clock.startedAt) + clock.scrubElapsedSec * 1000;
  }
  return realNowMs;
}

/** Elapsed seconds since T0 (never negative). */
export function elapsedSec(clock: IncidentClock, realNowMs: number): number {
  const elapsed = (now(clock, realNowMs) - Date.parse(clock.startedAt)) / 1000;
  return Math.max(0, Math.round(elapsed));
}

/** Switch to scrub mode, pinning the simulated elapsed time. */
export function toScrub(clock: IncidentClock, elapsedSeconds: number): IncidentClock {
  return { ...clock, mode: 'scrub', scrubElapsedSec: Math.max(0, elapsedSeconds) };
}

/** Return to real-time operation. */
export function toReal(clock: IncidentClock): IncidentClock {
  return { ...clock, mode: 'real' };
}

/** Step the scrub clock by a delta (seconds); switches into scrub mode. */
export function step(clock: IncidentClock, deltaSeconds: number): IncidentClock {
  const base = clock.mode === 'scrub' ? clock.scrubElapsedSec : 0;
  return toScrub(clock, base + deltaSeconds);
}

/** Format elapsed seconds as `H:MM:SS` (or `MM:SS` under an hour). */
export function formatElapsed(totalSec: number): string {
  const s = Math.max(0, Math.floor(totalSec));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const pad = (n: number) => n.toString().padStart(2, '0');
  return h > 0 ? `${h}:${pad(m)}:${pad(sec)}` : `${m}:${pad(sec)}`;
}
